import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { ActionTypeMapping, RoleEnum } from '../../../../application/common/enums/user-roles';
import { useAppDispatch } from '../../../../application/store/useStore';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid2';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
export function AddUserDialog({ openAddUserDialog, onCloseAddUser, handleAddUser, currentUserRole, companies }) {
    const { t } = useTranslation();
    const requiredMessage = t('requiredField');
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
        defaultValues: {
            companyIds: []
        }
    });
    const [newUser, setNewUser] = useState({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNo: '',
        role: RoleEnum.User,
        companyIds: []
    });
    useEffect(() => {
        if (!openAddUserDialog) {
            // Reset form when dialog closes
            reset({
                id: '',
                firstName: '',
                lastName: '',
                email: '',
                phoneNo: '',
                role: RoleEnum.User,
                companyIds: []
            });
            setNewUser({
                id: '',
                firstName: '',
                lastName: '',
                email: '',
                phoneNo: '',
                role: RoleEnum.User,
                companyIds: []
            });
        }
    }, [openAddUserDialog, reset]);
    return (_jsxs(Dialog, { maxWidth: 'sm', fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                overflow: 'hidden'
            }
        }, open: openAddUserDialog, onClose: onCloseAddUser, children: [_jsxs(DialogTitle, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }, children: [t('newUser'), _jsx(IconButton, { "aria-label": 'close', onClick: onCloseAddUser, sx: {
                            color: theme.palette.action.active
                        }, children: _jsx(CloseOutlined, {}) })] }), _jsx(DialogContent, { children: _jsx(Box, { display: 'flex', flexDirection: 'column', gap: '12px', pt: 1, children: _jsxs(Grid, { container: true, spacing: 1.5, children: [_jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Controller, { name: 'firstName', defaultValue: '', control: control, rules: {
                                            required: {
                                                value: true,
                                                message: requiredMessage
                                            }
                                        }, render: ({ field }) => _jsx(TextField, { fullWidth: true, label: t('firstName'), ...field }) }), errors.firstName && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.firstName.message }))] }), _jsxs(Grid, { size: { xs: 12, md: 6 }, children: [_jsx(Controller, { name: 'lastName', defaultValue: '', control: control, rules: {
                                            required: {
                                                value: true,
                                                message: requiredMessage
                                            }
                                        }, render: ({ field }) => _jsx(TextField, { fullWidth: true, label: t('lastName'), ...field }) }), errors.lastName && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.lastName.message }))] }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'email', defaultValue: '', control: control, rules: {
                                                required: {
                                                    value: true,
                                                    message: requiredMessage
                                                }
                                            }, render: ({ field }) => _jsx(TextField, { fullWidth: true, label: t('email'), ...field }) }), errors.email && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.email.message }))] }) }), _jsx(Grid, { size: { xs: 12, md: 6 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'phoneNo', defaultValue: '', control: control, rules: {
                                                required: {
                                                    value: true,
                                                    message: requiredMessage
                                                }
                                            }, render: ({ field }) => (_jsx(TextField, { fullWidth: true, label: t('phoneNumber'), ...field })) }), errors.phoneNo && (_jsx(Typography, { color: 'error', variant: 'caption', children: errors.phoneNo.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'companyIds', control: control, defaultValue: [], render: ({ field }) => (_jsx(Autocomplete, { multiple: true, options: companies, getOptionLabel: (option) => option.name, value: companies.filter((company) => field.value?.includes(company.id)), onChange: (_, selectedCompanies) => {
                                                    field.onChange(selectedCompanies.map((company) => company.id));
                                                }, renderInput: (params) => (_jsx(TextField, { ...params, fullWidth: true, label: t('companies') })) })) }), errors.companyIds && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.companyIds.message }))] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { children: [_jsx(Controller, { name: 'role', control: control, defaultValue: RoleEnum.User, rules: {
                                                required: {
                                                    value: true,
                                                    message: requiredMessage
                                                }
                                            }, render: ({ field }) => (_jsx(_Fragment, { children: _jsx(Autocomplete, { options: Object.values(RoleEnum)
                                                        .filter((value) => {
                                                        if (typeof value !== 'number')
                                                            return false;
                                                        // Show SuperAdmin role only if current user is SuperAdmin
                                                        if (value === RoleEnum.SuperAdmin && currentUserRole !== 'SuperAdmin') {
                                                            return false;
                                                        }
                                                        return true;
                                                    })
                                                        .map((role) => ({
                                                        label: ActionTypeMapping.get(role) || '',
                                                        value: role
                                                    })), getOptionLabel: (option) => option.label, value: Object.values(RoleEnum)
                                                        .filter((value) => typeof value === 'number')
                                                        .map((role) => ({
                                                        label: ActionTypeMapping.get(role) || '',
                                                        value: role
                                                    }))
                                                        .find((option) => option.value === field.value) || null, onChange: (_, selectedOption) => {
                                                        if (selectedOption) {
                                                            field.onChange(selectedOption.value);
                                                        }
                                                    }, renderInput: (params) => (_jsx(TextField, { ...params, fullWidth: true, label: `${t('role')}*` })) }) })) }), errors.role && (_jsx(Typography, { variant: 'caption', color: 'error', children: errors.role.message }))] }) })] }) }) }), _jsxs(DialogActions, { sx: {
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'white',
                    zIndex: 1000,
                    padding: '16px'
                }, children: [_jsx(Button, { onClick: onCloseAddUser, variant: 'outlined', color: 'inherit', autoFocus: true, children: t('cancel') }), _jsx(Button, { type: 'submit', variant: 'contained', onClick: handleSubmit(handleAddUser), children: t('add') })] })] }));
}
